import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolInvoke } from '@saas-pages/shared/public/runtime'
import { localeCode, useLocale, useT } from '@/shared/i18n'

const UTILITY = 'days-until-date'
const MS_PER_DAY = 1000 * 60 * 60 * 24
const MAX_LABEL_LENGTH = 80
const DEFAULT_TARGET_DAYS = 30

type DateStatus = 'future' | 'today' | 'past'

interface CountdownResult {
  target: Date
  daysRemaining: number
  absoluteDays: number
  wholeWeeks: number
  remainingDays: number
  approxWeeks: number
  status: DateStatus
}

function toLocalDate(value: string): Date | null {
  if (!value) return null
  const date = new Date(`${value}T00:00:00`)

  return Number.isNaN(date.getTime()) ? null : date
}

function todayLocalDate(): Date {
  const now = new Date()

  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

function toInputDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date)
  next.setDate(next.getDate() + days)

  return next
}

function formatDate(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(date)
}

function formatCalendarMonth(date: Date | null, locale: string): string {
  if (!date) return '---'

  return new Intl.DateTimeFormat(locale, { month: 'short' }).format(date)
}

function formatCalendarDay(date: Date | null): string {
  if (!date) return '--'

  return String(date.getDate()).padStart(2, '0')
}

function calculateCountdown(target: Date): CountdownResult {
  const today = todayLocalDate()
  const daysRemaining = Math.round((target.getTime() - today.getTime()) / MS_PER_DAY)
  const absoluteDays = Math.abs(daysRemaining)
  const wholeWeeks = Math.floor(absoluteDays / 7)
  const remainingDays = absoluteDays % 7
  const status: DateStatus = daysRemaining > 0
    ? 'future'
    : daysRemaining < 0
      ? 'past'
      : 'today'

  return {
    target,
    daysRemaining,
    absoluteDays,
    wholeWeeks,
    remainingDays,
    approxWeeks: Math.round((absoluteDays / 7) * 10) / 10,
    status,
  }
}

export function DaysUntilDatePage() {
  const t = useT()
  const { locale } = useLocale()
  const localeName = localeCode(locale)
  const [targetDate, setTargetDate] = useState('')
  const [label, setLabel] = useState('')

  useEffect(() => {
    setTargetDate(toInputDate(addDays(todayLocalDate(), DEFAULT_TARGET_DAYS)))
  }, [])

  const target = useMemo(() => toLocalDate(targetDate), [targetDate])
  const result = useMemo(() => target ? calculateCountdown(target) : null, [target])
  const labelValue = label.trim()

  const quickDates = useMemo(() => {
    const today = todayLocalDate()

    return [
      {
        key: 'week',
        label: t.daysUntilDate.quickWeek,
        date: toInputDate(addDays(today, 7)),
      },
      {
        key: 'month',
        label: t.daysUntilDate.quickMonth,
        date: toInputDate(addDays(today, 30)),
      },
      {
        key: 'year',
        label: t.daysUntilDate.quickYear,
        date: toInputDate(addDays(today, 365)),
      },
    ]
  }, [t.daysUntilDate.quickMonth, t.daysUntilDate.quickWeek, t.daysUntilDate.quickYear])

  const handleTargetChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setTargetDate(event.target.value)
  }, [])

  const handleLabelChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setLabel(event.target.value.slice(0, MAX_LABEL_LENGTH))
  }, [])

  const handleQuickDate = useCallback((date: string, key: string) => {
    setTargetDate(date)
    toolInvoke(UTILITY, { action: 'preset', preset: key })
  }, [])

  const handleClear = useCallback(() => {
    setTargetDate('')
    setLabel('')
    toolInvoke(UTILITY, { action: 'reset' })
  }, [])

  return (
    <div className="mx-auto max-w-5xl">
      <section className="pb-4 pt-1 sm:pb-8 sm:pt-4">
        <div className="mb-2 inline-flex rounded-md border bg-card px-2 py-0.5 text-xs font-medium text-muted-foreground shadow-sm sm:mb-3 sm:px-2.5 sm:py-1">
          {t.common.localBadge}
        </div>
        <h1 className="text-2xl font-bold tracking-normal sm:text-4xl">
          {t.daysUntilDate.title}
        </h1>
        <p className="mt-1 line-clamp-2 max-w-2xl text-sm leading-6 text-muted-foreground sm:mt-2 sm:line-clamp-none sm:text-base sm:leading-7">
          {t.daysUntilDate.description}
        </p>
      </section>

      <Card className="overflow-hidden">
        <div
          aria-hidden="true"
          className="h-1.5 bg-gradient-to-r from-emerald-400 via-primary to-amber-300"
        />
        <CardContent className="grid gap-0 p-0 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.95fr)] lg:grid-rows-[auto_1fr]">
          <div className="space-y-3 p-4 sm:p-6 lg:col-start-1 lg:row-start-1 lg:border-r lg:pb-0">
            <label
              htmlFor="days-until-target"
              className="block transition-colors focus-within:border-primary/50 sm:rounded-lg sm:border sm:bg-background/70 sm:p-3 sm:shadow-inner"
            >
              <span className="mb-1 block text-sm font-medium sm:mb-3">
                {t.daysUntilDate.targetDate}
              </span>
              <div className="grid gap-3 sm:grid-cols-[76px_minmax(10rem,1fr)] sm:items-center">
                <CalendarTile date={target} locale={localeName} />
                <input
                  id="days-until-target"
                  type="date"
                  value={targetDate}
                  onChange={handleTargetChange}
                  className="h-11 w-full min-w-0 rounded-md border border-input bg-card px-3 pr-10 text-base shadow-sm transition-colors focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring sm:h-10 sm:text-sm"
                />
              </div>
            </label>

            <div>
              <h2 className="mb-2 text-sm font-semibold tracking-normal">
                {t.daysUntilDate.quickDates}
              </h2>
              <div className="grid grid-cols-3 gap-2">
                {quickDates.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => handleQuickDate(item.date, item.key)}
                    className="min-h-10 rounded-md border bg-background/70 px-2 py-2 text-center text-sm font-medium transition-colors hover:border-primary/40 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <ResultPanel result={result} label={labelValue} locale={localeName} />

          <div className="space-y-4 border-t p-4 sm:p-6 lg:col-start-1 lg:row-start-2 lg:border-r lg:border-t-0">
            <label htmlFor="days-until-label" className="block">
              <span className="mb-2 block text-sm font-medium">
                {t.daysUntilDate.label}
              </span>
              <input
                id="days-until-label"
                type="text"
                value={label}
                maxLength={MAX_LABEL_LENGTH}
                onChange={handleLabelChange}
                placeholder={t.daysUntilDate.labelPlaceholder}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </label>

            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="ghost" onClick={handleClear}>
                {t.common.clear}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <section className="mt-10 grid gap-8 border-t pt-8 lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.7fr)]">
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.daysUntilDate.faqTitle}</h2>
          <dl className="mt-4 space-y-4">
            {t.daysUntilDate.faq.map((item) => (
              <div key={item.question}>
                <dt className="font-medium">{item.question}</dt>
                <dd className="mt-1 text-sm leading-6 text-muted-foreground">{item.answer}</dd>
              </div>
            ))}
          </dl>
        </div>

        <aside>
          <h2 className="text-xl font-semibold tracking-normal">{t.daysUntilDate.relatedTitle}</h2>
          <div className="mt-4 space-y-2">
            {t.daysUntilDate.related.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="block rounded-md border bg-card p-3 transition-colors hover:border-primary/40 hover:bg-accent"
              >
                <span className="text-sm font-medium">{item.title}</span>
                <span className="mt-1 block text-xs leading-5 text-muted-foreground">
                  {item.description}
                </span>
              </a>
            ))}
          </div>
        </aside>
      </section>
    </div>
  )
}

function CalendarTile({ date, locale }: { date: Date | null; locale: string }) {
  return (
    <span className="hidden overflow-hidden rounded-md border bg-card text-center shadow-sm sm:block">
      <span className="block bg-primary px-2 py-1 text-[0.65rem] font-semibold uppercase tracking-normal text-primary-foreground">
        {formatCalendarMonth(date, locale)}
      </span>
      <span className="block px-3 py-2 text-2xl font-bold leading-none tabular-nums">
        {formatCalendarDay(date)}
      </span>
    </span>
  )
}

function ResultPanel({
  result,
  label,
  locale,
}: {
  result: CountdownResult | null
  label: string
  locale: string
}) {
  const t = useT()

  if (!result) {
    return (
      <div className="flex flex-col justify-between gap-6 bg-secondary/30 p-4 sm:p-6 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:min-h-[360px]">
        <div>
          <p className="text-sm font-semibold text-primary">{t.daysUntilDate.resultReady}</p>
          <h2 className="mt-3 text-2xl font-bold tracking-normal">
            {t.daysUntilDate.emptyResultTitle}
          </h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.daysUntilDate.emptyResultDescription}
          </p>
        </div>
      </div>
    )
  }

  const statusLabel = result.status === 'future'
    ? t.daysUntilDate.statusFuture
    : result.status === 'past'
      ? t.daysUntilDate.statusPast
      : t.daysUntilDate.statusToday

  return (
    <div className="space-y-4 bg-secondary/30 p-4 sm:space-y-5 sm:p-6 lg:col-start-2 lg:row-span-2 lg:row-start-1">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold text-primary">{t.daysUntilDate.resultReady}</p>
          <span
            className={cn(
              'rounded-md border px-2 py-1 text-xs font-medium',
              result.status === 'future' && 'border-emerald-300 bg-emerald-100 text-emerald-950 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-emerald-200',
              result.status === 'today' && 'border-primary/30 bg-primary/10 text-foreground',
              result.status === 'past' && 'border-amber-300 bg-amber-100 text-amber-950 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200',
            )}
          >
            {statusLabel}
          </span>
        </div>
        <div className="mt-3 flex items-end gap-3">
          <span className="text-5xl font-bold leading-none tabular-nums tracking-normal sm:text-6xl">
            {result.daysRemaining}
          </span>
          <span className="pb-1 text-base font-medium text-muted-foreground">
            {t.daysUntilDate.calendarDays}
          </span>
        </div>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">
          {label
            ? `${t.daysUntilDate.labelPrefix} ${label}: ${formatDate(result.target, locale)}`
            : `${t.daysUntilDate.targetPrefix} ${formatDate(result.target, locale)}`}
        </p>
      </div>

      <div className="hidden rounded-lg border bg-card/80 p-4 shadow-sm sm:block">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold tracking-normal">{t.daysUntilDate.timeline}</h2>
          <span className="text-xs text-muted-foreground">
            {t.daysUntilDate.today} - {formatDate(result.target, locale)}
          </span>
        </div>
        <CountdownRail status={result.status} segmentCount={Math.max(1, Math.min(28, result.absoluteDays || 1))} />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Metric label={t.daysUntilDate.daysRemaining} value={result.daysRemaining} accent />
        <Metric
          label={t.daysUntilDate.weeksRemaining}
          value={`${result.wholeWeeks} ${t.daysUntilDate.weekShort} ${result.remainingDays} ${t.daysUntilDate.dayShort}`}
        />
        <Metric label={t.daysUntilDate.weeksApprox} value={result.approxWeeks.toString()} />
        <Metric label={t.daysUntilDate.status} value={statusLabel} />
      </div>

      <p className="text-xs leading-5 text-muted-foreground">
        {t.daysUntilDate.localNote}
      </p>
    </div>
  )
}

function CountdownRail({
  status,
  segmentCount,
}: {
  status: DateStatus
  segmentCount: number
}) {
  return (
    <div className="flex items-center gap-1" aria-hidden="true">
      {Array.from({ length: segmentCount }, (_, index) => (
        <span
          key={index}
          className={cn(
            'h-3 flex-1 rounded-full',
            status === 'future' && 'bg-emerald-400/60 dark:bg-emerald-300/50',
            status === 'today' && 'bg-primary/60',
            status === 'past' && 'bg-amber-300/80 dark:bg-amber-400/50',
          )}
        />
      ))}
    </div>
  )
}

function Metric({
  label,
  value,
  accent = false,
}: {
  label: string
  value: string | number
  accent?: boolean
}) {
  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2.5',
        accent ? 'bg-accent text-accent-foreground' : 'bg-background/70',
      )}
    >
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums tracking-normal">{value}</p>
    </div>
  )
}
